import React, { useState, useImperativeHandle, forwardRef } from "react";
import { cn } from "../../utils/cn";
import { AlertCircle, CheckCircle2, Info, AlertTriangle, X } from "lucide-react";

export const Alert = ({
  severity = "info",
  title,
  children,
  onClose,
  className
}) => {
  const configs = {
    info: {
      bg: "bg-blue-950/40 border-blue-800/60 text-blue-200",
      icon: Info,
      iconColor: "text-blue-400"
    },
    success: {
      bg: "bg-emerald-950/40 border-emerald-800/60 text-emerald-200",
      icon: CheckCircle2,
      iconColor: "text-emerald-400"
    },
    warn: {
      bg: "bg-amber-950/40 border-amber-800/60 text-amber-200",
      icon: AlertTriangle,
      iconColor: "text-amber-400"
    },
    warning: {
      bg: "bg-amber-950/40 border-amber-800/60 text-amber-200",
      icon: AlertTriangle,
      iconColor: "text-amber-400"
    },
    error: {
      bg: "bg-red-950/40 border-red-800/60 text-red-200",
      icon: AlertCircle,
      iconColor: "text-red-400"
    },
  };

  const current = configs[severity] || configs.info;
  const IconComponent = current.icon;

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3.5 rounded-xl border text-sm transition-all duration-200 shadow-sm",
        current.bg,
        className
      )}
    >
      <IconComponent className={cn("w-5 h-5 shrink-0 mt-0.5", current.iconColor)} />
      <div className="flex-1">
        {title && <h5 className="font-semibold text-white mb-0.5">{title}</h5>}
        <div className="text-xs sm:text-sm leading-relaxed opacity-90">{children}</div>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="text-white/60 hover:text-white p-1 rounded-md transition-colors bg-transparent border-none cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export const Messages = forwardRef((props, ref) => {
  const [messages, setMessages] = useState([]);

  useImperativeHandle(ref, () => ({
    show: (msg) => {
      const items = Array.isArray(msg) ? msg : [msg];
      const withIds = items.map((item) => ({
        ...item,
        id: item.id || Math.random().toString(36).substring(2, 9),
      }));

      setMessages((prev) => [...prev, ...withIds]);

      withIds.forEach((item) => {
        if (!item.sticky && item.life) {
          setTimeout(() => {
            setMessages((curr) => curr.filter((m) => m.id !== item.id));
          }, item.life || 3000);
        }
      });
    },
    clear: () => {
      setMessages([]);
    },
    replace: (msg) => {
      const items = Array.isArray(msg) ? msg : [msg];
      const withIds = items.map((item) => ({
        ...item,
        id: item.id || Math.random().toString(36).substring(2, 9),
      }));
      setMessages(withIds);
    }
  }));

  if (messages.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-2.5 my-3 w-full", props.className)}>
      {messages.map((m) => (
        <Alert
          key={m.id}
          severity={m.severity}
          title={m.summary}
          onClose={() => setMessages((curr) => curr.filter((item) => item.id !== m.id))}
        >
          {m.detail}
        </Alert>
      ))}
    </div>
  );
});

Messages.displayName = "Messages";
export default Alert;
